import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Divider } from '@material-ui/core';
import { useNavigate } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  root: {
    fontFamily: "'Inter', sans-serif",
    color: '#ffffff',
    padding: '26px 18px 0px',
    maxWidth: 980,
    margin: '0 auto',
    height: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    textAlign: 'left',

    [theme.breakpoints.down('sm')]: {
      padding: '10px 8px 0px',
    },
  },
  hero: {
    padding: '18px 18px 14px',
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.35)',
    background:
      'radial-gradient(circle at 18% 18%, rgba(77,121,255,0.35), rgba(10,14,30,0.96))',
    boxShadow: '0 0 24px rgba(77,121,255,0.14)',
    marginBottom: 18,

    [theme.breakpoints.down('sm')]: {
      padding: '10px 10px 10px',
      marginBottom: 10,
    },
  }, 
  titleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
  },
  title: {
    fontSize: '2.0rem',
    fontWeight: 900,
    letterSpacing: -0.4,
    lineHeight: 1.1,
    textShadow: '0 0 16px rgba(77, 121, 255, 0.55)',
  },
  subtitle: {
    marginTop: 8,
    color: '#cfd8ff',
    lineHeight: 1.6,
    fontSize: 13,
    maxWidth: 860,
    textAlign: 'left',
  },
  primaryButton: {
    backgroundColor: '#4d79ff',
    color: '#ffffff',
    textTransform: 'none',
    fontWeight: 800,
    borderRadius: 10,
    padding: '8px 12px',
    '&:hover': { backgroundColor: '#3e61cc' },
    [theme.breakpoints.down('sm')]: {
      fontSize: 11,
      padding: '6px 10px',
    },
  },
  section: {
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.22)',
    background: 'rgba(10, 14, 30, 0.82)',
    overflow: 'hidden',
    padding: 16,
    marginBottom: 14,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 900,
    letterSpacing: 0.35,
    textTransform: 'uppercase',
    color: '#e5e7ff',
    marginBottom: 10,
    textAlign: 'left',
  },
  body: {
    fontSize: 13,
    color: '#e8f5ff',
    lineHeight: 1.65,
    whiteSpace: 'pre-wrap',
    textAlign: 'left',
  },
  callout: {
    marginTop: 10,
    padding: 12,
    borderRadius: 12,
    backgroundColor: 'rgba(5, 8, 20, 0.88)',
    border: '1px solid rgba(158, 247, 197, 0.28)',
    color: '#e8f5ff',
    fontSize: 13,
    lineHeight: 1.65,
    textAlign: 'left',
  },
  calloutWarn: {
    marginTop: 10,
    padding: 12,
    borderRadius: 12,
    backgroundColor: 'rgba(5, 8, 20, 0.88)',
    border: '1px solid rgba(255, 180, 80, 0.35)',
    color: '#e8f5ff',
    fontSize: 13,
    lineHeight: 1.65,
    textAlign: 'left',
  },
  directionBadge: {
    display: 'inline-block',
    borderRadius: 6,
    padding: '2px 10px',
    fontWeight: 800,
    fontSize: 12,
    marginRight: 6,
    letterSpacing: 0.5,
  },
  badgeLong: {
    backgroundColor: '#2e7d32',
    color: '#fff',
  },
  badgeShort: {
    backgroundColor: '#c62828',
    color: '#fff',
  },
  badgeAvoid: {
    backgroundColor: '#616161',
    color: '#fff',
  },
  divider: {
    backgroundColor: 'rgba(255,255,255,0.10)',
    margin: '14px 0',
  },
  inlineCode: {
    fontFamily: 'monospace',
    backgroundColor: 'rgba(77, 121, 255, 0.15)',
    borderRadius: 4,
    padding: '1px 5px',
    fontSize: 12,
  },
  signalGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
    marginTop: 10,
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  signalBox: {
    padding: 12,
    borderRadius: 10,
    fontSize: 13,
    lineHeight: 1.6,
  },
  entryBox: {
    border: '1px solid rgba(46, 125, 50, 0.55)',
    backgroundColor: 'rgba(46, 125, 50, 0.08)',
    color: '#e8f5ff',
  },
  invalidationBox: {
    border: '1px solid rgba(198, 40, 40, 0.55)',
    backgroundColor: 'rgba(198, 40, 40, 0.08)',
    color: '#e8f5ff',
  },
  signalLabel: {
    fontWeight: 800,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 6,
  },
  entryLabel: { color: '#66bb6a' },
  invalidationLabel: { color: '#ef5350' },
}));

export default function ThesisGuidePage() {
  const classes = useStyles();
  const navigate = useNavigate();

  return (
    <div className={classes.root}>
      <div className={classes.hero}>
        <div className={classes.titleRow}>
          <div>
            <div className={classes.title}>Thesis Guide</div>
            <div className={classes.subtitle}>
              A practical walkthrough for reading, evaluating, and acting on agent-generated trade theses — from understanding conviction scores to drilling back into contributing stories.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button className={classes.primaryButton} variant="contained" onClick={() => navigate('/price-guide')}>
              Price Guide
            </Button>
            <Button className={classes.primaryButton} variant="contained" onClick={() => navigate('/methodology')}>
              Methodology
            </Button>
            <Button className={classes.primaryButton} variant="contained" onClick={() => navigate('/thesis-use-cases')}>
              Use Cases
            </Button>
            <Button className={classes.primaryButton} variant="contained" onClick={() => navigate('/thesis')}>
              Back to Theses
            </Button>
          </div>
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>1) What is a Trade Thesis?</div>
        <div className={classes.body}>
          {'A trade thesis is a structured, LLM-generated analytical brief that sits between raw on-chain stories and automated execution. It answers the question: "Given everything the agent has seen about this entity in the last few hours, is there a trade here?"\n\nEach thesis is the output of a two-phase process:\n\n- Phase 1 (Rule Engine): Scans recent stories every ~10 minutes, groups them by shared entity (token or wallet address), detects convergence patterns, and scores candidates.\n- Phase 2 (LLM Generator): Takes the top-scoring candidates hourly, bundles the contributing stories and market context, and calls gpt-4o for a structured thesis JSON.\n\nThe result is a thesis with a direction (LONG / SHORT / AVOID), a conviction score, a 2-3 sentence narrative, specific entry and invalidation signals, a time horizon, and risk factors — all grounded in the on-chain evidence.'}
        </div>
        <div className={classes.callout}>
          {'Architecture: Stories → Convergence Candidates → Agent Theses → /thesis dashboard\n\nTheses are not predictions. They are structured hypotheses derived from structural on-chain patterns. Always validate with your own judgment before acting.'}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>2) Reading a Thesis Card</div>
        <div className={classes.body}>
          {'Each card in the left panel gives you an at-a-glance summary of a thesis. Here\'s what each element means:\n\n'}
          <span className={`${classes.directionBadge} ${classes.badgeLong}`}>LONG</span>
          <span className={`${classes.directionBadge} ${classes.badgeShort}`}>SHORT</span>
          <span className={`${classes.directionBadge} ${classes.badgeAvoid}`}>AVOID</span>
          {'\n\n- LONG: The agent sees bullish structural signals — accumulation, cluster formation, surge confirmation.\n- SHORT: The agent sees bearish structural signals — distribution, thin liquidity, counter-trend surges.\n- AVOID: The agent sees danger signals — rug liquidity pull, wash trading loops, MEV sandwich patterns, or conflicting signals too ambiguous to trade.\n\nConviction Bar:\n- Gray fill (0.0–0.3): Speculative. Weak or noisy signals.\n- Amber fill (0.4–0.6): Moderate. Multiple signals converging but not fully confirmed.\n- Green fill (0.7–1.0): High conviction. Strong, consistent, multi-type signal convergence.\n\nTime Horizon: How long the thesis is expected to remain valid.\n- 1h: Very short-term signal (e.g., surge, immediate MEV pattern)\n- 4h: Medium-term structural pattern\n- 24h: Broader accumulation or distribution thesis\n- 7d: Macro positioning or campaign-level thesis\n\nStory Type Chips: The contributing story types that drove this thesis. More diverse types (WHALE + CLUSTER + SURGE) generally means higher-quality convergence than a single repeated story type.'}
        </div>
        <div className={classes.callout}>
          {'Underlying equation: /methodology → Thesis Integrity and Overlap Vector.\nThe √ affordance on thesis cards opens the measured breakdown inline.'}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>3) Entry and Invalidation Signals</div>
        <div className={classes.body}>
          {'Every thesis has two specific, falsifiable signals generated by the LLM:\n'}
        </div>
        <div className={classes.signalGrid}>
          <div className={`${classes.signalBox} ${classes.entryBox}`}>
            <div className={`${classes.signalLabel} ${classes.entryLabel}`}>Entry Signal</div>
            {'The specific on-chain event that confirms the thesis and suggests entry. Examples:\n"MEV Bot resumes net accumulation ≥$1B in NEAR or WETH within the next hour, confirmed by on-chain transfers"\n"Price breaks above $0.025 with volume above 1,000 tx/hr"\n"Whale cluster dissolves, then re-forms around the same token"'}
          </div>
          <div className={`${classes.signalBox} ${classes.invalidationBox}`}>
            <div className={`${classes.signalLabel} ${classes.invalidationLabel}`}>Invalidation Signal</div>
            {'The specific on-chain event that kills the thesis. Examples:\n"MEV Bot initiates net distribution ≥$5B in the next 4 hours"\n"Rabbit Cluster dissolves with n_nodes dropping below 100"\n"RUG_LIQUIDITY_PULL story detected on the token contract"\n"Whale starts distributing against the LONG thesis"'}
          </div>
        </div>
        <div className={classes.body} style={{ marginTop: 10 }}>
          {'These signals are generated by the LLM with specificity in mind — they reference actual addresses, metrics, and thresholds from the contributing stories. When you see a thesis you find interesting, monitor the /stories feed for events matching the entry or invalidation signals.'}
        </div>
        <div className={classes.calloutWarn}>
          {'Machine-checkable predicate coverage is surfaced on /methodology as a live OTA health counter.'}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>4) Understanding Conviction History</div>
        <div className={classes.body}>
          {'Theses are not static. A background tracker (Phase 3) runs every ~10 minutes and updates active theses based on new stories:\n\nConviction goes UP when:\n- Additional whale accumulation detected on a LONG thesis (+0.10 per event)\n- SURGE confirms a LONG thesis (+0.15, and status moves to "confirmed")\n- More cluster/funnel formation supports the structural thesis (+0.10)\n\nConviction goes DOWN when:\n- Whale distribution detected on a LONG thesis (−0.20)\n- Strong surge detected against a SHORT thesis (−0.30)\n- MEV sandwich activity on the entity (−0.05)\n\nThesis is INVALIDATED when:\n- RUG_LIQUIDITY_PULL detected on a LONG thesis (instant invalidation)\n- Conviction drops to ≤ 0.05 (auto-invalidated)\n\nThesis EXPIRES when:\n- The time horizon elapses without the thesis being confirmed or invalidated (e.g., a 24h thesis expires 24 hours after creation)\n\nThe conviction history sparkline in the detail panel shows how conviction has evolved since creation. A declining sparkline on an active thesis is a warning signal. A rising sparkline suggests the agent is seeing confirming evidence.'}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>5) Contributing Stories — the Evidence Chain</div>
        <div className={classes.body}>
          {'Every thesis traces back to the on-chain stories that triggered it. The "Contributing Stories" section in the detail panel lists these stories by ID, with "View in Stories" links that take you directly to the /stories page with that story selected.\n\nHow to use the evidence chain:\n\nStep A — Check story types for convergence quality\n- A thesis built on WHALE + CLUSTER + SURGE represents multi-type convergence — the most reliable signal pattern.\n- A thesis built solely on repeated WHALE stories might be a single actor doing something unusual, not necessarily a coordinated pattern.\n\nStep B — Open contributing stories and review the evidence graphs\n- Click "View in Stories" for any contributing story to open it in the stories feed.\n- From there, open the Evidence Summary to inspect the graph structure.\n- Look for: do the same wallets/addresses appear across multiple contributing stories? If yes, that\'s the strongest form of convergence.\n\nStep C — Cross-validate with the /links graph\n- From any story\'s Evidence Summary, open the dedicated Evidence Graph (/links) to do deeper investigation.\n- Check if the structure the agent cited (cluster, funnel, whale accumulation) is actually visible in the graph topology.'}
        </div>
        <div className={classes.callout}>
          {'Group Mode tip: Select multiple contributing stories in /stories and enable Group Mode to overlay their evidence graphs. Shared addresses that appear across all contributing stories are the highest-signal leads for the thesis.'}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>6) Risk Factors</div>
        <div className={classes.body}>
          {'Each thesis includes a risk factors list generated by the LLM — specific concerns that could undermine the thesis even if it appears technically valid. Common risk factors you\'ll see:\n\n⚠️ "High anomaly in prior distribution could signal manipulation or liquidation"\n  → The entity had a suspicious large distribution recently, which the agent flagged as a warning.\n\n⚠️ "MEV Bot activity may be front-running unrelated to genuine token accumulation"\n  → The apparent accumulation pattern might be MEV extraction rather than informed buying.\n\n⚠️ "Bridge to Arbitrum introduces cross-chain risks like failed transfers or exploits"\n  → The structural pattern involves bridge activity which carries execution and security risk.\n\n⚠️ "Thin liquidity — fewer than 10 participants in the price surge"\n  → The SURGE story has very few unique addresses, suggesting a manufactured move.\n\nAlways read the risk factors before considering action. A high-conviction LONG thesis with 3+ severe risk factors should be treated with much more caution than the conviction score alone suggests.'}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>7) Annotations and Position Tracking</div>
        <div className={classes.body}>
          {'The "Your Notes" panel (visible for all authenticated users) lets you annotate any thesis with your own research:\n\nStatus options:\n- none: Default. You\'ve seen it but haven\'t made a decision.\n- watching: You\'re monitoring this thesis and its updates.\n- acted_on: You took a position based on this thesis.\n- passed: You reviewed it and decided not to act.\n\nOverride Conviction:\n- You can set your own conviction score (0–1) independent of the agent\'s score.\n- Useful when you have additional off-chain context the agent doesn\'t have access to.\n\nNotes:\n- Free-form text for your own research notes. Reference wallet addresses, price levels, external events, or reasons for your decision.\n\nPosition Tracking (Premium / Enterprise only):\n- Entry Price: The price at which you entered the position.\n- Position Size: Free text — "$50K", "2 ETH", "500 tokens", etc.\n- Exit Price: The price at which you exited.\n- All position data is saved securely to your account and only visible to you.'}
        </div>
        <div className={classes.callout}>
          {'All annotations are private. No other user can see your notes, status, or position tracking data.'}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>8) Trade Actions (Premium / Enterprise)</div>
        <div className={classes.body}>
          {'For premium and enterprise users, the detail panel includes a Trade Actions section with quick-access links:\n\n- Open on Uniswap: Opens Uniswap V3 with the token address pre-filled as the output currency (swap ETH → token).\n- Open on 1inch: Opens 1inch with the token address pre-filled (swap ETH → token).\n- Copy Address: Copies the entity address to clipboard for use in other tools (Etherscan, block explorers, execution systems).\n\nSafety note: Trade Actions links are provided for convenience. Always verify the token contract address independently before executing a swap — the DEX interface cannot distinguish between a legitimate token and a scam contract at the same address.\n\nFor addresses that are wallets rather than tokens (entity_type = "address"), the Uniswap and 1inch links may not be meaningful. In that case, use Copy Address and investigate the wallet in Etherscan or the /links graph.'}
        </div>
        <div className={classes.calloutWarn}>
          {'⚠️ DEX links use the entity_address from the thesis. If the thesis is about a wallet address (e.g., a whale or MEV bot) rather than a token contract, swapping is not applicable. Always confirm the entity type shown in the thesis header.'}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>9) Filters and Status Flow</div>
        <div className={classes.body}>
          {'Thesis Status Flow:\n\nactive → confirmed: A positive confirming signal arrives (e.g., SURGE confirms a LONG thesis)\nactive → invalidated: A hard invalidation signal arrives (e.g., rug pull, conviction drops to ≤0.05)\nactive → expired: The time horizon elapses without confirmation or invalidation\nconfirmed → invalidated: Even confirmed theses can be invalidated by strong counter-signals\nconfirmed → expired: A confirmed thesis that reaches its time horizon without being explicitly closed\n\nDefault filter: "Active + Confirmed" — shows only theses that are currently actionable.\n\nDirection filter:\n- All: Show every thesis regardless of direction\n- LONG only: Focus on bullish setups\n- SHORT only: Focus on bearish setups\n- AVOID only: Review the danger/manipulation signals the agent is flagging\n\nSort options:\n- Conviction (default): Highest-conviction theses first — the agent\'s strongest calls.\n- Most Recent: Latest updated theses first — useful for monitoring rapidly evolving situations.\n\nSearch:\n- Searches across token symbol, entity name, entity address, and thesis text.\n- Use this to quickly find any thesis involving a specific token or address you\'re tracking.'}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>10) Candidate Pipeline (Premium / Enterprise)</div>
        <div className={classes.body}>
          {'The Candidate Pipeline section (collapsible, premium+ only) shows entities that the rule engine has flagged as convergence candidates but which the LLM has not yet promoted to a full thesis.\n\nCandidates are created every ~10 minutes. The LLM promotion step runs hourly. So for up to an hour before a thesis is published, you can see the candidate in the pipeline — with its convergence score, matched rules, and contributing stories — before the LLM writes the narrative.\n\nHow to use candidates:\n- A candidate with a high convergence score (≥5.0) and several matched rules is likely to become a thesis within the hour.\n- Review the matched rules (e.g., "whale_accumulation_then_cluster", "multi_whale_same_token") to pre-evaluate the likely direction before the thesis is generated.\n- Candidates with "rug_pull_detected" or "cluster_with_loop" rules are warnings to watch even before they become AVOID theses.\n\nCandidates are deduplicated by entity address and hour bucket. Seeing the same address appear as a candidate multiple hours in a row is a notable signal of sustained convergence.'}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>11) Suggested Evaluation Checklist</div>
        <div className={classes.body}>
          {'Before acting on a thesis:\n\n□ Is the conviction score ≥ 0.5? (Moderate or higher)\n□ Is the time horizon appropriate for my strategy?\n□ Have I read all risk factors and can I live with each one?\n□ Have I checked at least 2–3 contributing stories in /stories to verify the narratives?\n□ Does the evidence graph support the structural claim (cluster, whale accumulation, funnel)?\n□ Are the entry and invalidation signals specific enough to monitor?\n□ For LONG theses: Is the entity a tradeable token (not just a wallet address)?\n□ For AVOID theses: Do I have any exposure to this entity that I need to reduce?\n□ Has conviction been stable or rising? (Check the history sparkline)\n□ Have I set my annotation (watching / acted_on) so I can track this thesis over time?'}
        </div>
        <Divider className={classes.divider} />
        <div className={classes.body}>
          {'Thesis → Stories → /links is the recommended investigation chain. Start with the thesis for the structured hypothesis, validate with contributing stories for the narrative evidence, and drill into /links for the raw graph structure.'}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>5) How Convergence Is Measured</div>
        <div className={classes.body}>
          {'Convergence is no longer just "some rules matched." Each thesis stores a six-part overlap vector:\n\nO_t = lambda_addr·O_address + lambda_tok·O_token + lambda_contract·O_contract + lambda_venue·O_venue + lambda_time·O_time + lambda_motif·O_motif\n\nThe six bars on the thesis detail page show whether the supporting stories are actually converging on the same addresses, tokens, contracts, venues, time cluster, and behavioral motif. A thesis with broad overlap across multiple dimensions is structurally stronger than one where only a single rule happened to fire.\n\nThesis integrity is then decomposed directly on the page:\nC^Theta_t = 0.25·Q_S + 0.25·O + 0.15·D + 0.15·H + 0.10·X - 0.10·R\n\nWhere Q_S is supporting-story quality, O is overlap, D is story-family diversity, H is thesis history strength, X is supporting depth, and R is risk pressure.'}
        </div>
        <div className={classes.callout}>
          {'Hysteresis state machine\n\ncold -> warming -> active -> confirmed\n  ^        |         |          |\n  |        v         v          v\n  +---- cooling <----+---- invalidated\n\nThe tracker does not flip states on a single noisy tick. It only activates on the way up when C^Theta_t clears tau_up, and only cools on the way down when it drops through tau_down. That gap reduces thesis flicker.'}
        </div>
      </div>
    </div>
  );
}
